'use client'

import { useEffect } from 'react'
import { Button, Dialog, Icon, Navigation } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import Auth from '../Auth'
import useAuthModal from '../../hooks/useAuthModal/useAuthModal'
import useAuthTracking from '../../hooks/useAuthTracking'
import useAuthenticationContext from '../../hooks/useAuthenticationContext'
import { AuthenticationProvider } from '../../containers/authentication/AuthenticationProvider'
import { ComponentLocation } from '../../observability/constants'
import AuthenticationErrorBoundary from '../AuthenticationErrorBoundary'
import { RegistrationBehaviourTrackingProvider } from '../../containers/registration-behaviour-tracking'
import useRegistrationBehaviourTracking from '../../hooks/useRegistrationBehaviourTracking'

export const AuthModal = () => {
  const translate = useTranslate('auth')
  const { trackClickEvent } = useAuthTracking()
  const { resetAuthView } = useAuthenticationContext()
  const { isAuthModalOpen, closeAuthModal } = useAuthModal()
  const { trackRegistrationBehaviourEnd } = useRegistrationBehaviourTracking()

  useEffect(() => {
    if (!isAuthModalOpen) return

    resetAuthView()
  }, [resetAuthView, isAuthModalOpen])

  const handleButtonClose = () => {
    trackClickEvent({ target: ClickableElement.CloseSelectTypeModal, targetDetails: 'button' })
    trackRegistrationBehaviourEnd({ actionDetails: 'modal-closed' })

    closeAuthModal()
  }

  if (!isAuthModalOpen) return null

  return (
    <Dialog
      testId="auth-modal"
      show
      hasScrollableContent
      className="auth__container"
      aria={{
        labelledby: 'auth_modal_title',
      }}
    >
      <div className="u-fill-width">
        <Navigation
          right={
            <Button
              iconName={X24}
              iconColor={Icon.Color.GreyscaleLevel1}
              styling={Button.Styling.Flat}
              inline
              onClick={handleButtonClose}
              aria={{ 'aria-label': translate('a11y.close') }}
            />
          }
        />
        <div className="u-overflow-auto" aria-live="polite" aria-relevant="additions">
          <Auth componentLocation={ComponentLocation.AuthenticationModal} />
        </div>
      </div>
    </Dialog>
  )
}

const AuthModalWithProvider = () => (
  <AuthenticationProvider>
    <AuthenticationErrorBoundary componentLocation={ComponentLocation.AuthenticationModal}>
      <RegistrationBehaviourTrackingProvider isModal>
        <AuthModal />
      </RegistrationBehaviourTrackingProvider>
    </AuthenticationErrorBoundary>
  </AuthenticationProvider>
)

export default AuthModalWithProvider
